// The source catalog. Every other language must define every key (checked by TypeScript).
// "{name}" placeholders are filled in by t(); keep them in the translations.
export const en = {
  "nav.home": "LEARNABLE home",
  "nav.main": "Main",
  "nav.courses": "Courses",
  "nav.review": "Review",
  "nav.progress": "Progress",
  "nav.guide": "Guide",
  "nav.marketplace": "Marketplace",
  "nav.streak": "Daily streak: {n}",
  "nav.xp": "Total experience: {n} XP",

  "account.menu": "Account",
  "account.settings": "Settings",
  "account.guide": "How LEARNABLE works",
  "account.signOut": "Sign out",

  "common.save": "Save",
  "common.saved": "Saved.",
  "common.saving": "Saving…",
  "common.cancel": "Cancel",
  "common.close": "Close",
  "common.pleaseWait": "Please wait…",

  "signin.title": "Sign in to LEARNABLE",
  "signin.registerTitle": "Create your account",
  "signin.email": "Email",
  "signin.password": "Password",
  "signin.passwordRule": "At least 12 characters.",
  "signin.submit": "Sign in",
  "signin.register": "Create account",
  "signin.forgot": "Forgot your password?",
  "signin.toRegister": "Create an account",
  "signin.toSignIn": "I already have an account",
  "signin.language": "Language",

  "password.current": "Current password",
  "password.new": "New password (12+ characters)",
  "password.change": "Change password",
  "password.changed": "Password changed. Your other devices were signed out.",

  "settings.title": "Settings",
  "settings.intro": "Your profile and preferences. They are saved to your account and apply on every device.",
  "settings.profile": "Profile",
  "settings.displayName": "Display name",
  "signin.emailOrUsername": "Email or username",
  "signin.username": "Username (optional)",
  "signin.usernameHint": "3–30 letters, digits, dots, underscores or hyphens. You can sign in with it instead of your email.",
  "settings.username": "Username",
  "settings.usernameHint": "Optional. Lets you sign in with this name instead of your email. Others never see it. Leave empty to remove it.",
  "settings.displayNameHint": "Shown instead of your email. Leave it empty to use your email.",
  "settings.email": "Email",
  "settings.emailHint": "The address you sign in with.",
  "settings.language": "Language",
  "settings.languageHint": "The language of menus and pages. Your course material and the AI's feedback stay in the course's own language.",
  "settings.translationNote": "Translation is in progress: some screens are still in English.",
  "settings.study": "Study",
  "settings.timezone": "Time zone",
  "settings.timezoneHint": "Your study day, streak and daily goal follow this time zone.",
  "settings.dailyGoal": "Daily goal",
  "settings.dailyGoalHint": "Answers per day.",
  "settings.security": "Security",
  "settings.signOutHint": "Signing out removes your account's data from this browser.",

  "guide.title": "How LEARNABLE works",
  "guide.intro": "LEARNABLE turns your own study material into questions, checks your answers and schedules each review just before you would forget.",
  "guide.contents": "Contents",
  "guide.structure.title": "1. How a course is organised",
  "guide.structure.body": "A course is divided into chapters, chapters into topics, and topics into concepts. Each concept holds learning items: the facts to remember, each asked through one or more questions.",
  "guide.material.title": "2. Add your material",
  "guide.material.body": "Open a course and upload PDF, Word, PowerPoint, text or Markdown files, or paste text. If you already have questions and answers, upload them with lines starting with \"Question:\" and \"Answer:\" (or \"Domanda:\" and \"Risposta:\"): each becomes a concept, without using AI.",
  "guide.curriculum.title": "3. Build the curriculum",
  "guide.curriculum.body": "Ask the AI to propose chapters, topics and concepts from your material. Review the proposal, rename, move or remove anything, then accept it. Nothing changes until you accept.",
  "guide.study.title": "4. Study a concept",
  "guide.study.body": "Open a concept and press Activate: its learning items are prepared from your material. Read them, then press \"I have studied this concept\". Each question is asked until you've answered it well three times (not necessarily in a row), with feedback after every answer.",
  "guide.answer.title": "5. Answer and get feedback",
  "guide.answer.body": "Write your answer in your own words, or press \"Speak your answer\" to dictate it. A hint is available but halves the XP. The AI checks the meaning, not the exact words, and shows four scores and a checklist of what was right, missing or wrong. The reference answer and its sources are always one click away.",
  "guide.disagree.title": "6. If you disagree",
  "guide.disagree.body": "Press \"Disagree with the grade?\". You can explain why and ask the AI for a second opinion (both stay visible), or simply grade the answer yourself. Your own grade always wins.",
  "guide.review.title": "7. Review on time",
  "guide.review.body": "Every answer moves its question on a schedule: the better you know it, the longer until it comes back. The Review page and your dashboard show what is due and about how long it takes.",
  "guide.progress.title": "8. Track your progress",
  "guide.progress.body": "Your streak counts the days you studied, XP rewards correct answers, and the daily goal sets your pace. \"What you keep getting wrong\" collects the mistakes found in your recent answers, with a practice button.",
  "guide.organise.title": "9. Keep things tidy",
  "guide.organise.body": "In \"Manage questions\" you can select single questions or whole chapters, topics and concepts to delete, pause, resume or move them, edit a question, and drag ⠿ to reorder. Long sections fold away with the arrow beside their title.",
  "guide.marketplace.title": "10. Share and get courses",
  "guide.marketplace.body": "In the Marketplace you find courses made by other users, by category, level, language and size. Before adding one you see its page: what it covers, who it's for, what you'll learn, its chapters and how many questions it has. \"Add to my courses\" puts it on your dashboard: its author keeps the questions up to date and you can't edit them, but your study is your own (your plan, answers, XP, and the priority you give each question). The author's material can't be downloaded. To share a course of yours, open it and write its \"Marketplace page\", save it as a draft, then publish.",
  "guide.glossary.title": "Words used in the app",
  "guide.term.concept": "Concept",
  "guide.term.concept.def": "One idea of your course, such as a definition or a rule.",
  "guide.term.item": "Learning item",
  "guide.term.item.def": "One fact to remember inside a concept, with its questions and expected answer.",
  "guide.term.active": "Active",
  "guide.term.active.def": "The concept takes part in study and reviews. Paused or not studied concepts don't.",
  "guide.term.grades": "Again · Hard · Good · Easy",
  "guide.term.grades.def": "How well you knew the answer. It decides when the question comes back.",
  "guide.term.mastery": "Mastery",
  "guide.term.mastery.def": "An estimate of how well you remember a concept, from your reviews.",
  "guide.term.xp": "XP and streak",
  "guide.term.xp.def": "Points for correct answers, and the number of days in a row you studied.",
  "guide.privacy.title": "Your data",
  "guide.privacy.body": "Your courses, answers and progress are visible only to you. Dictation is transcribed by your browser; only the text is kept.",
  "help.button": "Help: {topic}",
  "help.more": "Learn more",
  "help.nextStep": "The single most useful thing to do now. Reviews that are due always come first, then new material.",
  "help.courses": "One card per course. The green button reviews what is due now; the blue button continues with something new.",
  "help.streak": "Days in a row with at least one answer. Answer one question today to keep it going.",
  "help.xp": "Experience points, earned for correct answers. Using a hint halves them.",
  "help.goal": "How many answers you want to give each day. You can change it in Settings.",
  "help.planner": "How many questions come due by each time, and about how long they take.",
  "help.coursePlanner": "This course only: how many of its questions come due when, and about how long they take. New material needs at least three good answers per question.",
  "help.activity": "One square per day: the darker it is, the more you answered.",
  "help.concept": "A concept is one idea of your course. Start studying it to prepare its questions, read its learning items, then practise them.",
  "help.consolidate": "Press the button when you have read the learning items below. Each question is asked until you've answered it well three times, so it sticks.",
  "help.study": "Write the answer in your own words or speak it. The AI checks the meaning, not the exact words, and shows what was right or missing.",
  "help.questions": "Tick single questions or a whole chapter, topic or concept, then delete, pause or move them. Drag ⠿ to change the order.",
  "help.marketplace": "Courses made by other users. Open one to see what it covers and how many questions it has; add it to study it with your own plan. Its author keeps it up to date.",
  "help.weakSpots": "Mistakes found in your latest answers, grouped by concept. Practise them until they disappear from this list.",
  "help.material": "Upload PDF, Word, PowerPoint or text files, or paste text. LEARNABLE builds the course and its questions from it.",
  "start.title": "How LEARNABLE works",
  "start.intro": "Four steps. Open any of them to see it in the guide.",
  "start.hide": "Got it, hide this",
  "start.reopen": "Show how LEARNABLE works",
  "start.material.title": "Add your material",
  "start.material.body": "Create a course and upload your notes, slides or books, or paste text.",
  "start.plan.title": "Approve the plan",
  "start.plan.body": "The AI proposes chapters and concepts. Change anything, then accept.",
  "start.study.title": "Study and answer",
  "start.study.body": "Read a concept, then answer its questions in your own words or by voice.",
  "start.review.title": "Review on time",
  "start.review.body": "Questions come back just before you would forget them. Follow the green button.",
  "nav.community": "Community",
  "community.title": "Community",
  "community.intro": "See who is earning the most experience, follow other learners and cheer them on.",
  "community.tab.leaderboard": "Leaderboard",
  "community.tab.friends": "Friends",
  "community.period": "Period",
  "community.scope": "Who",
  "community.period.day": "Today",
  "community.period.week": "This week",
  "community.period.month": "This month",
  "community.period.all": "All time",
  "community.scope.everyone": "Everyone",
  "community.scope.friends": "Friends",
  "community.leader": "Leader",
  "community.xp": "{n} XP",
  "community.you": "You",
  "community.yourPlace": "Your place",
  "community.empty": "No one has earned XP in this period yet.",
  "community.emptyFriends": "Follow other learners to compare with them here.",
  "community.windowNote": "Days run on UTC time; weeks start on Monday. Only people who joined the community are listed.",
  "community.joinTitle": "Join the community",
  "community.joinText": "You're not on the leaderboard yet. Turn on community visibility to appear on it, be found and be followed. Others see only your display name and your XP, never your email.",
  "community.joinButton": "Show me in the community",
  "community.needName": "Choose a display name in Settings first.",
  "community.follow": "Follow",
  "community.following": "Following",
  "community.friend": "Friends",
  "community.followsYou": "Follows you",
  "community.since": "Learning here since {date}",
  "community.statXpTotal": "Total XP",
  "community.statXpWeek": "XP this week",
  "community.statStreak": "Day streak",
  "community.statFollowers": "Followers",
  "community.statFollowing": "Following",
  "community.followingList": "People you follow",
  "community.followersList": "Your followers",
  "community.searchLabel": "Find learners by name",
  "community.searchPlaceholder": "Search by display name",
  "community.search": "Search",
  "community.noResults": "No one found. Only people who joined the community can be found.",
  "community.noFollowing": "You aren't following anyone yet.",
  "community.noFollowers": "No followers yet.",
  "community.streakDays": "{n}-day streak",
  "community.xpToday": "{n} XP today",
  "community.notFound": "This profile isn't available.",
  "community.seeLeaderboard": "See the community leaderboard",
  "course.leaderboardLink": "Course leaderboard",
  "course.leaderboardTitle": "Course leaderboard",
  "course.leaderboardIntro": "XP earned in this course by everyone who has it.",
  "course.leaderboardPrivate": "This is a private course, so only you are on its leaderboard. Courses from the Marketplace are shared with everyone who has them.",
  "course.leaderboardParticipants": "{n} learners share this course.",
  "settings.community": "Community",
  "settings.communityToggle": "Show me in the community (leaderboards, search and following)",
  "settings.communityHint": "Off by default. Others see only your display name, your XP and your streak, never your email, courses or answers. You need a display name to turn it on.",
  "study.repeat.badge": "Almost there: repeat it",
  "study.repeat.title": "Review the reference answer, then repeat it",
  "study.repeat.body": "Your answer was strong on most points. Read the reference answer above, then write it again in your own words. If you repeat it well, it counts as a correct answer and your schedule moves forward. If not, the item goes back to level zero.",
  "study.repeat.label": "Your answer, again",
  "study.repeat.submit": "Repeat it",
  "study.repeat.selfGrade": "Grade it yourself instead",
  "study.repeat.evalTitle": "How you repeated it",
  "error.generic": "Something went wrong. Please try again.",
  "error.transport": "Can't reach the server. Check your connection and try again.",
  "error.session": "Your session has expired. Please sign in again.",
  "error.validation": "Some of the information entered isn't valid.",
  "error.notFound": "This item no longer exists.",
  "error.tooLarge": "This file is too large.",
  "error.unsupported": "This file type isn't supported. Use PDF, Word, PowerPoint, text or Markdown files.",
  "error.aiNotConfigured": "The AI isn't set up on the server yet.",
  "error.aiUnavailable": "The AI is temporarily unavailable. Please try again later.",
  "error.aiInvalid": "The AI gave an unusable response. Please try again.",
  "error.state": "That action isn't possible in the current state.",
  "error.server": "Something went wrong on the server. Please try again.",
  "error.rateLimited": "Too many attempts. Please wait a little and try again.",
  "error.managedCourse": "This course comes from the marketplace: its author keeps it up to date, so its content can't be changed here.",
  "studyState.NOT_STUDIED": "Not studied",
  "studyState.STUDIED": "Studied",
  "studyState.ACTIVE": "Active",
  "studyState.PAUSED": "Paused",
  "studyState.COMPLETED": "Completed",
  "memoryState.NEW": "New",
  "memoryState.LEARNING": "Learning",
  "memoryState.REVIEW": "In review",
  "memoryState.RELEARNING": "Relearning",
  "memoryState.MASTERED": "Mastered",
  "outcome.AGAIN": "Again",
  "outcome.HARD": "Hard",
  "outcome.GOOD": "Good",
  "outcome.EASY": "Easy",
  "outcomeHelp.AGAIN": "I didn't know it",
  "outcomeHelp.HARD": "I knew it, with difficulty",
  "outcomeHelp.GOOD": "I knew it",
  "outcomeHelp.EASY": "I knew it easily",
  "classification.CORRECT": "Correct",
  "classification.PARTIALLY_CORRECT": "Partly correct",
  "classification.MISCONCEPTION": "Misconception",
  "classification.WRONG": "Not correct",
  "classification.UNCERTAIN": "Uncertain",
  "role.CORE_TRAINABLE": "Core",
  "role.SUPPORTING_TRAINABLE": "Supporting",
  "role.COMMON_TRAP": "Common trap",
  "role.INFORMATIONAL": "Informational",
  "role.REFERENCE": "Reference",
  "role.OPTIONAL_EXTENSION": "Optional",
  "priority.1": "Essential",
  "priority.2": "Important",
  "priority.3": "Extra",
  "label.page": "Page {n}",
  "label.pages": "Pages {a}–{b}",
  "label.pageShort": "p. {n}",
  "label.pagesShort": "pp. {a}–{b}",
  "category.law": "Law",
  "category.economics_business": "Economics & business",
  "category.accounting_finance": "Accounting & finance",
  "category.medicine_health": "Medicine & health",
  "category.sciences": "Sciences",
  "category.mathematics": "Mathematics",
  "category.engineering": "Engineering",
  "category.computer_science": "Computer science",
  "category.languages": "Languages",
  "category.humanities": "History & humanities",
  "category.arts": "Arts & music",
  "category.professional_exams": "Professional exams",
  "category.other": "Other",
  "level.all": "All levels",
  "level.beginner": "Beginner",
  "level.intermediate": "Intermediate",
  "level.advanced": "Advanced",
  "size.small": "Short (under 50 questions)",
  "size.medium": "Medium (50–299 questions)",
  "size.large": "Large (300+ questions)",
  "sort.popular": "Most taken",
  "sort.newest": "Newest",
  "sort.largest": "Most questions",
  "lang.it": "Italian",
  "lang.en": "English",
  "price.free": "Free",
  "common.loading": "Loading…",
  "common.tryAgain": "Try again",
  "common.dismiss": "Dismiss",
  "common.delete": "Delete",
  "common.edit": "Edit",
  "common.rename": "Rename",
  "common.title": "Title",
  "common.description": "Description",
  "common.continue": "Continue",
  "common.search": "Search",
  "common.add": "Add",
  "common.remove": "Remove",
  "common.yes": "Yes",
  "common.no": "No",
  "common.collapse": "Collapse {label}",
  "common.expand": "Expand {label}",
  "drag.item": "Item",
  "drag.reorder": "Reorder {label}",
  "drag.pickedUp": "Picked up {label}, position {pos} of {total}.",
  "drag.over": "{label} is now at position {pos} of {total}.",
  "drag.dropped": "{label} dropped at position {pos} of {total}.",
  "drag.droppedPlain": "{label} dropped.",
  "drag.cancelled": "Moving {label} cancelled.",
  "drawing.loadFailed": "The drawing couldn't be loaded.",
  "course.authorOrder": "The author sets the order of this course.",
  "priority.yours": "Your priority",
  "priority.label": "Priority",
  "priority.author": "Author: {name}",
  "priority.useAuthor": "Use the author's",
  "editTitle.rename": "Rename {label}",
  "label.course": "course",
  "label.chapter": "chapter",
  "label.topic": "topic",
  "label.concept": "concept",
  "library.heading": "My courses",
  "library.count.one": "{n} course",
  "library.count.other": "{n} courses",
  "library.shownOf": "{shown} of {total} shown",
  "library.search": "Search courses",
  "library.show": "Show",
  "library.sortBy": "Sort by",
  "library.new": "+ New course",
  "library.marketplace": "🛒 Marketplace",
  "library.empty": "No courses yet. Create one, then add your study material or your own questions and answers.",
  "library.createFirst": "Create your first course",
  "library.noMatch": "No course matches this search or filter.",
  "libStatus.all": "All courses",
  "libStatus.in_progress": "In progress",
  "libStatus.not_started": "Not started",
  "libStatus.due": "Reviews due",
  "libStatus.completed": "Nothing new to learn",
  "libStatus.paused": "Paused",
  "libStatus.archived": "Archived",
  "libSort.recent_study": "Recently studied",
  "libSort.recent_created": "Recently created",
  "libSort.alphabetical": "Alphabetical",
  "libSort.most_due": "Most reviews due",
  "card.marketplacePill": "🛒 From the marketplace · by {author}",
  "card.marketplaceHint": "Its author keeps the content up to date; your study is your own.",
  "card.continue": "Continue",
  "card.next": "Next",
  "card.archived": "Archived",
  "card.paused": "Paused",
  "card.conceptsStudied": "concepts studied",
  "card.itemsIntroduced": "learning items introduced",
  "card.moreFor": "More for {title}",
  "menu.details": "Course details",
  "menu.material": "Study material",
  "menu.progress": "Progress",
  "menu.publish": "🛒 Publish to the marketplace",
  "menu.pause": "Pause",
  "menu.resume": "Resume",
  "menu.archive": "Archive",
  "menu.restore": "Restore",
  "menu.delete": "Delete…",
  "menu.confirmOwn": "Delete \"{title}\"? Its chapters, questions, material and your answers are deleted for good. Archive it instead to keep everything.",
  "menu.confirmMarketplace": "Delete \"{title}\"? You can add it again from the Marketplace for free.",
  "newCourse.aria": "New course",
  "newCourse.language": "Language of the material",
  "newCourse.create": "Create course",
  "learn.resume.label": "Resume practice",
  "learn.resume.hint": "Continue the round you left unfinished",
  "learn.study.label": "Learn next",
  "learn.study.hint": "Study {title}",
  "learn.study.hintNone": "Learn new material",
  "learn.activate.label": "Start learning",
  "learn.activate.hint": "Prepare the questions of {title} and start studying it",
  "learn.activate.hintNone": "Start studying the next concept",
  "learn.setup.label": "Add material",
  "learn.setup.hint": "Add study material or your own questions to start",
  "learn.none.label": "All learned ✓",
  "learn.none.hint": "Everything in this course has been learned",
  "review.none": "✓ Nothing to review now",
  "review.noneTitle": "Nothing in this course needs reviewing right now",
  "review.aria.one": "Review {n} item",
  "review.aria.other": "Review {n} items",
  "review.button": "Review",
  "course.practiceHard": "Practice hard questions",
  "course.chapters": "Chapters",
  "course.chaptersEmpty": "Add a chapter, then add study material to it.",
  "course.chapterMeta": "{active} of {total} concepts active · mastery {mastery}",
  "course.newChapter": "New chapter title",
  "course.addChapter": "Add chapter",
  "course.curriculum": "Curriculum",
  "course.fallback": "Course",
  "course.allMaterial": "All study material",
  "course.manageQuestions": "Manage questions",
  "course.weakSpots": "What you keep getting wrong",
  "chapter.gone": "This chapter no longer exists.",
  "chapter.topics": "Topics",
  "chapter.starting": "Starting analysis…",
  "chapter.analyze": "Analyze new material",
  "chapter.analyzeHint": "The AI proposes topics and concepts from material not analyzed yet. You review the proposal before anything changes.",
  "chapter.noMaterial": "There's no new study material to analyze in this chapter. Upload material first.",
  "chapter.empty": "No topics yet. Upload material below, then analyze it.",
  "chapter.topicMeta": "{active} of {total} concepts active",
  "topic.gone": "This topic no longer exists.",
  "topic.concepts": "Concepts",
  "topic.empty": "No concepts in this topic.",
  "topic.needsReview": "Needs source review",
  "topic.conceptMeta": "{state} · mastery {mastery}",
  "unit.item.one": "{n} item",
  "unit.item.other": "{n} items",
  "unit.answer.one": "{n} answer",
  "unit.answer.other": "{n} answers",
  "consolidate.continue": "Continue consolidation",
  "consolidate.unfinished": "Your batch of {items} is unfinished: {left} left. Answers you've already given are kept.",
  "consolidate.resume": "Resume",
  "consolidate.doneTitle": "Consolidated",
  "consolidate.doneBody": "Every item in this concept has had its consolidation rounds. They come back in your reviews when they're due.",
  "consolidate.title": "Studied this concept?",
  "consolidate.body": "Read what to study above first. When you're ready, each question is asked until you've answered it well {n} times (not necessarily in a row), with feedback after every answer.",
  "consolidate.batch": "This batch: {items} · {answers}",
  "consolidate.more.one": "{n} more item waits for a later batch",
  "consolidate.more.other": "{n} more items wait for a later batch",
  "consolidate.button": "I have studied this concept",
  "breadcrumb.aria": "Breadcrumb",
  "dash.title": "Dashboard",
  "dash.loading": "Loading your dashboard…",
  "dash.aside": "Your study",
  "dash.today": "Today",
  "dash.xpTodayPlus": "+{n} XP today",
  "dash.goal": "Goal {done}/{target}",
  "dash.tzNote": "Your study days (streak, goal, XP today) follow {zone}. This device is in {device}.",
  "dash.tzUse": "Use {zone}",
  "next.title": "Your next step",
  "next.resumeTitle": "Continue consolidation · Good answer {round} of {total}",
  "next.answersLeft": "{n} answers left",
  "next.resume": "Resume",
  "next.minutes": "about {n} min",
  "next.startReview": "Start review",
  "next.learnTitle": "Continue learning: {concept}",
  "next.nextConcept": "next concept",
  "next.learn": "Learn",
  "next.setupTitle": "Set up {concept}",
  "next.theNextConcept": "the next concept",
  "next.setupDetail": "{course} · activate it to prepare its questions",
  "next.setup": "Set up",
  "next.addMaterialTitle": "Add material to {course}",
  "next.yourCourse": "your course",
  "next.addMaterialDetail": "Upload study material or your own questions and answers to build its concepts.",
  "next.firstCourse": "Start your first course",
  "next.firstCourseDetail": "Create a course, then add material or your own questions.",
  "next.caughtUp": "You're all caught up",
  "next.caughtUpDetail": "Nothing is due and there's no new material waiting.",
  "streak.title": "Daily streak",
  "streak.unit.one": "day",
  "streak.unit.other": "days",
  "streak.done": "Today complete. Come back tomorrow to keep it going.",
  "streak.keep": "Answer one question today to keep your streak.",
  "streak.start": "Answer a question today to start a streak.",
  "streak.last7": "The last 7 days",
  "streak.studied": "studied",
  "streak.noStudy": "no study",
  "xp.title": "Experience",
  "xp.how": "How XP works",
  "xp.howBody": "Each correct answer to the same learning item earns more: 10 XP the first time, then 20, 30… up to 150. A hint halves the XP for that answer. Wrong answers earn nothing but don't reset your progress. XP rewards practice; it never changes your review schedule.",
  "goal.title": "Daily goal",
  "goal.sr": "Daily goal: {done} of {target} completed answers today, {percent} percent.",
  "goal.answersToday": "{done} / {target} answers today",
  "goal.perDay": "Answers per day",
  "goal.edit": "Edit goal",
  "goal.invalid": "Choose a whole number from 1 to 500.",
  "planner.title": "Time planner",
  "planner.now": "Now",
  "planner.1h": "In 1 hour",
  "planner.4h": "In 4 hours",
  "planner.1d": "In 1 day",
  "planner.3d": "In 3 days",
  "planner.7d": "In 7 days",
  "planner.dueNow": "{n} due now: about {min} min",
  "planner.caption": "Reviews due by each time, including overdue ones",
  "planner.dueBy": "Due by",
  "planner.items": "Items",
  "planner.hint": "Based on your current review schedule; changes as you study. Each row includes everything due before it.",
  "activity.title": "Activity",
  "activity.seeMore": "See more",
  "activity.future": "{day}: in the future",
  "activity.day": "{day}: {answers}",
  "activity.dayXp": "{day}: {answers}, {xp} XP",
  "activity.aria": "Activity calendar: {days}, {answers}",
  "activity.summary": "{days} · {answers}",
  "unit.activeDay.one": "{n} active day",
  "unit.activeDay.other": "{n} active days",
  "activity.less": "Less",
  "activity.more": "More",
  "activity.legendFuture": "Future",
  "pb.curriculumHint": "How much of the material you have studied and activated.",
  "pb.conceptsActive": "Concepts active",
  "pb.activeOf": "{active} of {total}",
  "pb.memory": "Memory",
  "pb.memoryHint": "How well you retain what you train. Mastery is an estimate from your reviews.",
  "pb.mastery": "Mastery (estimate)",
  "pb.itemsTrained": "Items in training",
  "pb.markedHard": "Marked hard",
  "pb.dueNow": "Due now",
  "pb.laterToday": "Later today",
  "pb.tomorrow": "Tomorrow",
  "pb.next7": "Next 7 days",
  "pb.newToLearn": "New to learn",
  "pb.overdue.one": "{n} question is overdue: start with those.",
  "pb.overdue.other": "{n} questions are overdue: start with those.",
  "pb.caption": "Questions coming due in this course, and about how long they take",
  "pb.when": "When",
  "pb.questions": "Questions",
  "pb.time": "Time",
  "pb.minutes": "~{n} min",
  "study.intent.LEARN": "Learn",
  "study.intent.SCHEDULED_REVIEW": "Review",
  "study.intent.PRACTICE": "Practice",
  "study.intent.EXAM": "Exam",
  "study.intent.CONSOLIDATION": "Consolidation",
  "study.round": "Good answer {n} of {total}",
  "study.progressAria": "Progress in this session",
  "study.progress": "{n} of {total}",
  "study.xpSessionAria": "{n} XP this session",
  "study.ownReview": "Own review: no change to your plan, no XP",
  "study.leaveResume": "Leave (resume later)",
  "study.end": "End session",
  "study.preparing": "Preparing your session…",
  "study.empty.title": "Nothing to study right now",
  "study.empty.body": "Nothing qualifies for this session. Activate a concept to add new material, or come back when reviews are due.",
  "study.empty.back": "Back to the course",
  "study.newMaterial": "New material",
  "study.keyPoints": "Key points",
  "study.sources": "Sources",
  "study.ready": "I'm ready: test me",
  "study.answering": "Answering",
  "study.drawHint": "Draw your answer below. The AI compares it with the reference drawing.",
  "study.note": "Note (optional)",
  "study.yourAnswer": "Your answer",
  "study.notePlaceholder": "Anything to add to your drawing…",
  "study.answerPlaceholder": "Answer in your own words…",
  "study.stopListening": "Stop listening",
  "study.speak": "Speak your answer",
  "study.evaluating": "Evaluating…",
  "study.submit": "Submit",
  "study.skip": "Skip this question",
  "study.dictationOn": "Dictation is transcribed by your browser, which may send the audio to its speech service. Only the text is kept; edit it before you submit.",
  "study.dictationOff": "Voice answers need Chrome, Edge or Safari. You can type your answer here.",
  "study.drawingCompare": "Your drawing and the reference",
  "study.yourDrawing": "Your drawing",
  "study.reference": "Reference",
  "study.gradedByYou": "Graded by you",
  "study.needsGrade": "Needs your grade",
  "study.firstEval": "First evaluation",
  "study.secondOpinion": "Second opinion",
  "study.afterObjection": "After your objection: “{text}”",
  "study.referenceSummary": "Reference answer and sources",
  "study.studyConcept": "📖 Study this concept",
  "study.gradeHow": "How well did you know it?",
  "study.gradeYourself": "Or grade it yourself",
  "study.yourGrade": "Your grade",
  "study.retryEval": "Try the evaluation again",
  "study.skipNow": "Skip for now",
  "study.disagree": "Disagree with the grade?",
  "study.notMemorized": "Not memorized yet: you'll see it again in this session.",
  "study.levelNext": "Level {from} → {to}. Next review: {when}",
  "study.practiceNote": "Practice doesn't change your review schedule.",
  "study.complete": "Session complete",
  "study.xpSessionPlus": "+{n} XP this session",
  "study.answeredIn": "{answers} in about {min} min.",
  "study.wentWell": "{n} went well.",
  "study.steady": "Steady work: this is settling in.",
  "study.comeBack": "The ones that went badly come back soon, which is how they stick.",
  "study.stillDue": "Still due now: {n} (about {min} min).",
  "study.nothingDue": "Nothing else is due right now.",
  "study.done": "Done",
  "study.noXp": "This answer doesn't earn XP (practice, or not due for review).",
  "study.correctXp": "Correct answer: +{n} XP",
  "study.halfXp": "(hint used: half XP)",
  "study.hintLabel": "Hint",
  "study.noHint": "No hint for this question.",
  "study.showHint": "Show hint",
  "study.hintHalves": "Using a hint halves the XP for this answer.",
  "study.revealHint": "Reveal hint",
  "study.plusZero": "+0 XP",
  "study.onlyCorrectSelf": "Only correct answers earn XP: your own Hard, Good or Easy counts too.",
  "study.onlyCorrect": "Only fully correct answers earn XP. Your progress towards the next award is kept.",
  "study.awardCorrect": "Correct",
  "study.awardHint": "Hint used",
  "study.ordinal": "correct answer #{n} for this item",
  "study.ordinalHalf": "correct answer #{n} for this item, half of {base}",
  "study.batchDone": "Batch consolidated",
  "study.correctAnswers": "Correct answers",
  "study.xpThisSession": "XP this session",
  "study.whatNext": "What happens next",
  "study.whatNextBody": "Items whose last round was right come back for review in about 4 hours; the others wait for the next batch.",
  "study.waiting.one": "{n} more item is waiting",
  "study.waiting.other": "{n} more items are waiting",
  "study.batchNotAll": "This batch is done, not the whole concept: {waiting}.",
  "study.allRounds": "Every item in this concept that was waiting has been answered well three times.",
  "study.nextBatch": "Next batch",
  "study.conceptCosts": "Looking at the concept now counts as using a hint: this answer earns half XP.",
  "study.openConcept": "Open the concept",
  "study.dontRemember": "📖 Don't remember? Study this concept",
  "study.refDrawing": "The reference drawing",
  "dispute.title": "Ask the AI to look again",
  "dispute.testAi": "The test AI can't read your objection: a second opinion would repeat the same grade. Grade it yourself below.",
  "dispute.why": "Why do you disagree?",
  "dispute.placeholder": "Say what the evaluation got wrong…",
  "dispute.hint": "The AI sees your objection and evaluates again. Both opinions stay visible; your grade changes only if you choose it below.",
  "dispute.asking": "Asking…",
  "dispute.ask": "Ask for a second opinion",
  "eval.correct": "Correct",
  "eval.complete": "Complete",
  "eval.understanding": "Understanding",
  "eval.precise": "Precise",
  "eval.Strong": "Strong",
  "eval.Partial": "Partial",
  "eval.Weak": "Weak",
  "eval.scoreBreakdown": "Score breakdown",
  "eval.right": "Right",
  "eval.missing": "Missing",
  "eval.misconception": "Misconception",
  "eval.materialSays": "The material says",
  "eval.covered": "What your answer covered",
  "eval.failed": "The answer couldn't be evaluated.",
  "eval.aiEvaluation": "AI evaluation: {verdict}",
  "eval.insufficient": "The course material doesn't cover this well enough to grade it.",
  "eval.testAiTitle": "Test AI.",
  "eval.testAiBody": "This grade comes from a stand-in that only compares words with the reference: it can't understand paraphrases or objections. Check the reference and grade it yourself, or connect a real AI.",
  "conceptPanel.title": "Study this concept",
  "conceptPanel.next": "I've studied it — next question",
  "conceptPanel.closeAria": "Close the concept",
  "conceptPanel.loading": "Loading the concept…",
  "conceptPanel.openFull": "Open the full concept page in a new tab",
  "dictation.blocked": "The microphone is blocked. Allow it in your browser's site settings to dictate.",
  "dictation.network": "Speech recognition needs an internet connection.",
  "dictation.noMic": "No microphone was found.",
  "dictation.stopped": "Speech recognition stopped unexpectedly. You can keep typing.",
  "concept.activate": "Activate",
  "concept.activateHelp": "Prepares this concept's questions from your material so you can study it.",
  "concept.markStudied": "Mark as studied",
  "concept.markStudiedHelp": "You studied it elsewhere: it is noted, without preparing questions yet.",
  "concept.activateAgainHelp": "Studies it again: its questions come back into your sessions.",
  "concept.pause": "Pause",
  "concept.pauseHelp": "Stops its questions for a while. Nothing is lost.",
  "concept.deactivate": "Deactivate",
  "concept.deactivateHelp": "Takes it out of study and reviews. Your answers and history are kept.",
  "concept.resume": "Resume",
  "concept.resumeHelp": "Brings its questions back into study and reviews.",
  "concept.sourceDeleted": "The material this concept came from was deleted. Keep, edit or delete it.",
  "concept.manage": "Manage this concept",
  "concept.loadingItems": "Loading learning items…",
  "concept.preparing": "Preparing learning items…",
  "concept.itemsFailed": "Learning items couldn't be prepared.",
  "concept.noSource": "This concept has no source material to build learning items from.",
  "concept.whatToStudy": "What to study",
  "concept.reviewOwnHelp": "Be tested on this concept whenever you like. It doesn't change your review plan and earns no XP.",
  "concept.reviewOwn": "🔁 Review on your own",
  "concept.readThen": "Read these, then press \"I have studied this concept\" below to practise them.",
  "concept.testMe": "🔁 Test me on this",
  "concept.hideDetails": "Hide details",
  "concept.details": "Details",
  "concept.level": "Level {n}",
  "concept.markedHard": "Marked hard",
  "concept.notTraining": "Not in training",
  "concept.state": "State",
  "concept.levelLabel": "Level",
  "concept.nextReview": "Next review",
  "concept.reviews": "Reviews",
  "concept.lapses": "Lapses",
  "concept.inTraining": "In training",
  "concept.questions": "Questions",
  "concept.sourcesTitle": "Sources",
  "concept.loadingSources": "Loading sources…",
  "concept.noSources": "No source passages.",
  "concept.misconceptions": "Recurring misconceptions",
  "drawing.answerBy": "Answer by drawing",
  "drawing.studentsDraw": "Students answer this question by drawing. The AI compares their drawing with this one.",
  "drawing.youDraw": "You answer this question by drawing. The AI compares your drawing with this one.",
  "drawing.referenceAlt": "Reference drawing",
  "drawing.explain": "For questions answered with a drawing (a molecule, a diagram, a graph): add the correct drawing, and students will draw their answer instead of writing it.",
  "drawing.save": "Save this drawing",
  "drawing.replace": "Replace with an image…",
  "drawing.upload": "Upload the drawing…",
  "drawing.drawNew": "Draw a new one",
  "drawing.drawHere": "Draw it here",
  "drawing.words": "Answer in words instead",
  "drawing.tools": "Drawing tools",
  "drawing.pen": "✏️ Pen",
  "drawing.eraser": "🧽 Eraser",
  "drawing.thick": "Thick line",
  "drawing.thin": "Thin line",
  "drawing.undo": "↶ Undo",
  "drawing.clear": "Clear",
  "drawing.area": "Drawing area: draw your answer here",
  "drawing.photoUpload": "Drew it on paper? Upload a photo instead",
  "drawing.photoError": "That file isn't an image this browser can read. Try a JPEG or PNG photo.",
  "drawing.photoAlt": "Your photo of the drawing",
  "drawing.drawInstead": "Draw instead",
  "q.title": "Questions",
  "q.introOwn": "Select questions to delete, pause, resume or move them; drag ⠿ to reorder them; edit one to fix its title, wording or expected answer.",
  "q.introManaged": "This course comes from the marketplace: its author keeps the questions up to date. Select questions to pause or resume them, or give them your own priority (only you see it).",
  "q.search": "Search questions",
  "q.searchPlaceholder": "Search questions and answers",
  "q.allPriorities": "All priorities",
  "q.priorityOnly": "{label} only ({n})",
  "q.deselectAll": "Deselect all",
  "q.selectAll": "Select all ({n})",
  "q.selectAllShown": "Select all shown ({n})",
  "q.collapseAll": "Collapse all",
  "q.expandAll": "Expand all",
  "q.selectedAria": "Selected questions",
  "q.selectedCount": "{n} selected",
  "q.moveTo": "Move to…",
  "q.setPriority": "Set priority",
  "q.setPriorityPlaceholder": "Set priority…",
  "q.reviewSelectedHelp": "Be tested on the selected questions now. It doesn't change your review plan and earns no XP.",
  "q.clearSelection": "Clear selection",
  "q.verb.delete": "Deleted",
  "q.verb.pause": "Paused",
  "q.verb.resume": "Resumed",
  "q.verb.move": "Moved",
  "q.verb.set_priority": "Updated",
  "unit.question.one": "{n} question",
  "unit.question.other": "{n} questions",
  "unit.chapter.one": "{n} chapter",
  "unit.chapter.other": "{n} chapters",
  "unit.topic.one": "{n} topic",
  "unit.topic.other": "{n} topics",
  "unit.concept.one": "{n} concept",
  "unit.concept.other": "{n} concepts",
  "unit.group.one": "{n} emptied group",
  "unit.group.other": "{n} emptied groups",
  "q.newConcepts.one": "{n} new concept",
  "q.newConcepts.other": "{n} new concepts",
  "q.removedConcepts.one": "{n} empty concept removed",
  "q.removedConcepts.other": "{n} empty concepts removed",
  "q.noticeBulk": "{verb} {questions}{extra}.",
  "q.noticeDeleted": "Deleted {questions}.",
  "q.noticeDeletedGroups": "Deleted {questions} and {groups}.",
  "q.confirmDelete": "Delete {questions}? Their answers and review history are deleted too.{also}",
  "q.confirmAlso": " The {groups} left with no questions are deleted too.",
  "q.emptySearch": "No question matches this search.",
  "q.empty": "No questions yet. Activate a concept or import your own questions and answers.",
  "q.clearToReorder": "Clear the search to reorder these questions.",
  "q.select": "Select: {text}",
  "q.otherWordings.one": "+{n} other wording",
  "q.otherWordings.other": "+{n} other wordings",
  "q.editAria": "Edit question",
  "q.titleLabel": "Title (shown in lists)",
  "q.wordings": "Question wordings",
  "q.wording": "Wording {n}",
  "q.addWording": "+ Add another wording",
  "q.expected": "Expected answer",
  "q.prio1": "Essential: the core, must know",
  "q.prio2": "Important: should know",
  "q.prio3": "Extra: addendum, deeper detail",
  "q.keyPoints": "Key points (one per line, up to 10)",
  "q.moveAria": "Move questions",
  "q.moveHeading": "Move {questions} to",
  "q.choose": "Choose a topic or a concept…",
  "q.topicOption": "Topic: {title} (each question keeps its own concept)",
  "q.conceptOption": "   ↳ into concept: {title}",
  "q.removeEmpty": "Remove concepts left without questions",
  "q.moveHint": "Questions keep their review progress. In a concept that isn't active, they aren't reviewed until it is.",
  "q.move": "Move",
  "q.groupSelect": "Select every question in {label}",
  "mat.chapterTitle": "Chapter material",
  "mat.collapseLabel": "study material",
  "mat.files.one": "{n} file",
  "mat.files.other": "{n} files",
  "mat.uploading": "Uploading…",
  "mat.upload": "Upload material",
  "mat.uploadQa": "Upload questions & answers",
  "mat.paste": "Paste text",
  "mat.howTo": "How to prepare questions & answers",
  "mat.howToBody": "Upload a PDF, Word, text or Markdown file where each question starts with “Domanda:” and its expected answer with “Risposta:” (or “Question:” / “Answer:”), at the beginning of a line. Headings group the questions into topics.",
  "mat.howToBody2": "Each question becomes a concept marked not studied, with your answer as the expected answer. Activate it to start reviewing. No AI is used for the import.",
  "mat.loading": "Loading material…",
  "mat.none": "No material yet. Upload PDF, Word, PowerPoint, text or Markdown files.",
  "mat.file": "File",
  "mat.status": "Status",
  "mat.passages": "Passages",
  "mat.added": "Added",
  "mat.actions": "Actions",
  "mat.qa": "Q&A",
  "mat.processing": "Processing…",
  "mat.ready": "Ready",
  "mat.failed": "Failed",
  "mat.download": "Download",
  "mat.confirmDelete": "Delete {name}? Concepts built from it will be flagged for review.",
  "mat.pasteAria": "Paste text",
  "mat.pastePlaceholder": "e.g. Lecture 3 notes",
  "mat.thisTextIs": "This text is",
  "mat.asMaterial": "Study material",
  "mat.asQa": "Questions & answers (Domanda: / Risposta:)",
  "mat.text": "Text",
  "mat.textPlaceholderQa": "# Topic\nDomanda: …?\nRisposta: …",
  "mat.textPlaceholder": "# Heading\nYour notes…",
  "mat.adding": "Adding…",
  "mat.addText": "Add text",
  "mat.pastedName": "Pasted text {when}",
  "prop.title": "Proposed curriculum",
  "prop.reading": "The AI is reading your material…",
  "prop.applied": "This proposal has already been applied.",
  "prop.failed": "The AI couldn't propose a curriculum.",
  "prop.blank": "Every chapter, topic and concept needs a title.",
  "prop.longMaterial": "The material was long: this covers its first {used} of {total} passages.",
  "prop.dropped": "{n} proposed concepts were left out because they couldn't be tied to your material.",
  "prop.hint": "Edit anything below: rename, reorder, move concepts between topics, merge, delete or add. Nothing is activated: you choose what to study afterwards.",
  "prop.chapterTitle": "Chapter title",
  "prop.deleteChapter": "Delete chapter",
  "prop.accept": "Accept curriculum",
  "prop.reject": "Reject proposal",
  "prop.topicAria": "Topic {title}",
  "prop.existingTopic": "existing topic",
  "prop.topicTitle": "Topic title",
  "prop.moveTopicUp": "Move topic up",
  "prop.moveTopicDown": "Move topic down",
  "prop.deleteTopic": "Delete topic",
  "prop.conceptTitle": "Concept title",
  "prop.exists": "already exists",
  "prop.moveConceptUp": "Move concept up",
  "prop.moveConceptDown": "Move concept down",
  "prop.moveToTopic": "Move to topic",
  "prop.untitled": "Untitled topic",
  "prop.merge": "Merge with next",
  "prop.addConcept": "+ Add concept",
  "mkt.intro": "Courses made by other LEARNABLE users. Add one to your courses and study it with your own schedule: its author keeps it up to date. To share one of yours, open it and fill in its “Marketplace page”.",
  "mkt.categories": "Categories",
  "mkt.all": "All",
  "mkt.searchPlaceholder": "Search by title or description",
  "mkt.level": "Level",
  "mkt.language": "Language",
  "mkt.size": "Size",
  "mkt.sort": "Sort",
  "mkt.any": "Any",
  "mkt.loading": "Loading courses…",
  "mkt.noMatch": "No course matches these filters yet.",
  "mkt.publishedByMe": "Published by me",
  "mkt.status.DRAFT": "Draft",
  "mkt.status.PUBLISHED": "Published",
  "mkt.status.UNPUBLISHED": "Not published",
  "mkt.version": "version {n}",
  "mkt.students.one": "{n} student",
  "mkt.students.other": "{n} students",
  "mkt.editCourse": "Edit the course and its page",
  "mkt.by": "by {author}",
  "mkt.inYourCourses": "In your courses",
  "mkt.loadingCourse": "Loading course…",
  "mkt.updated": "updated {date}",
  "mkt.statStudents": "Students",
  "mkt.yourCourse": "Your course",
  "mkt.draftTag": "draft",
  "mkt.notPublishedTag": "not published",
  "mkt.open": "Open in my courses",
  "mkt.addBack": "Add back to my courses",
  "mkt.add": "Add to my courses · {price}",
  "mkt.about": "About this course",
  "mkt.learn": "What you'll learn",
  "mkt.audience": "Who it's for",
  "mkt.hiddenNote": "The questions and answers become visible once the course is in your courses. You study them inside LEARNABLE: the author's material can't be downloaded.",
  "sales.originTitle": "🛒 From the marketplace",
  "sales.madeBy": "Made by {author} (version {version}). Its author keeps the chapters, questions and answers up to date, so you can't edit them here: updates reach you automatically.",
  "sales.yourStudy": "Your study is your own: activating concepts, pausing, your answers, your review plan and XP, and the priority you give each question (only you see it).",
  "sales.seePage": "See its marketplace page",
  "sales.title": "🛒 Marketplace page",
  "sales.pPublished": "Published · version {n}",
  "sales.pNot": "Not published",
  "sales.pDraft": "Draft",
  "sales.pNone": "Not shared",
  "sales.explain": "Share this course with other users. Here you write its page, the only thing people see before adding it: what it covers, who it's for, what they'll learn. They also see the chapter titles and how many questions there are; the questions themselves only once they have it, and your uploaded material never.",
  "sales.seeAsOthers": "See it as others do",
  "sales.edit": "Edit the marketplace page",
  "sales.write": "Write a marketplace page",
  "sales.subtitle": "Subtitle",
  "sales.subtitleHint": "(one line, e.g. “All 300 oral exam questions, with model answers”)",
  "sales.category": "Category",
  "sales.description": "Description",
  "sales.descriptionHint": "(what it covers, how you made it, how to use it)",
  "sales.outcomes": "What students will learn",
  "sales.outcomesHint": "(one per line, up to 8)",
  "sales.tags": "Tags",
  "sales.tagsHint": "(comma separated, up to 8)",
  "sales.saveDraft": "Save draft",
  "sales.savePage": "Save page",
  "sales.publish": "Publish",
  "sales.publishUpdate": "Publish an update",
  "sales.publishHintUpdate": "Sends your current chapters, questions and answers to everyone who has the course. Their own progress is kept.",
  "sales.publishHint": "Makes the course visible in the Marketplace. It's free for now. You can keep editing it and publish updates later.",
  "sales.rights": "The questions and answers are mine to share (I wrote them, or I have the rights to the material they come from).",
  "sales.publishing": "Publishing…",
  "sales.publishUpdateBtn": "Publish update",
  "sales.unpublish": "Unpublish",
  "sales.noteSaved": "Saved: the marketplace page shows it now.",
  "sales.noteDraft": "Draft saved. Nobody else sees it until you publish.",
  "sales.notePublishedV": "Published version {n}: everyone who has the course now gets your latest questions.",
  "sales.notePublished": "Published! Other users can now find it in the Marketplace.",
  "sales.noteUnpublished": "Unpublished: new people can't find it. Those who already have it keep it.",
  "prog.empty": "Create a course and start learning to see your progress.",
  "prog.weakConcepts": "Weak concepts",
  "prog.weakMeta": "lapses: {lapses} · marked hard: {hard}",
  "prog.byChapter": "By chapter and topic",
  "prog.colChapter": "Chapter / topic",
  "weak.intro": "Mistakes found in your recent answers, by concept. They disappear once your latest answers to the item no longer show them. Practice doesn't change your review schedule.",
  "weak.loading": "Looking at your recent answers…",
  "weak.empty": "No recurring mistakes right now. Keep answering and they will show up here.",
  "weak.practiceTop.one": "Practice the top {n} concept",
  "weak.practiceTop.other": "Practice the top {n} concepts",
  "weak.practiceThis": "Practice this",
  "weak.seen.one": "seen {n} time, last {date}",
  "weak.seen.other": "seen {n} times, last {date}",
  "home.intro": "Scheduled reviews keep what you've learned. Due counts use the same rules as a review session.",
  "home.noCourses": "No courses yet.",
  "home.createOnDashboard": "Create one on your dashboard",
  "home.meta": "{due} due now · {introduced} of {trained} items introduced",
  "activity.pageIntro": "Completed answers per day over the last year, in your timezone. An answer counts once it has a final grade, whether it was right or wrong.",
  "reset.title": "Reset your password",
  "reset.intro": "Enter your account's email. We'll send a link to choose a new password; it works once, for 30 minutes.",
  "reset.send": "Send reset link",
  "reset.sentMessage": "If an account exists for this email, a link to reset its password is on its way. Check your inbox (and spam folder).",
  "reset.back": "Back to sign in",
  "reset.chooseTitle": "Choose a new password",
  "reset.incomplete": "This link is incomplete. Open the link from the email again, or ask for a new one.",
  "reset.done": "Your password has been changed. Every device was signed out: sign in with the new password.",
  "reset.newPassword": "New password",
  "reset.repeat": "Repeat the new password",
  "reset.tooShort": "Use at least {n} characters.",
  "reset.mismatch": "The two passwords don't match.",
  "reset.set": "Set new password",
  "source.aria": "Source passage",
  "source.closeAria": "Close source",
  "source.loading": "Loading passage…",
  "source.open": "Open the source passage",
  "course.loadingCurriculum": "Loading curriculum…",
  "mkt.addPlain": "Add to my courses",
  "sales.pageName": "Marketplace page",
} as const;

export type MessageKey = keyof typeof en;
export type Messages = Record<MessageKey, string>;
